import { useId, useMemo, useRef, useState } from 'react';
import { useDropdownMenu } from '../../../hooks';
import {
  normalizeOptions,
  moveHighlightIndex,
  handleSelectTriggerKeyDown,
  normalizeValues,
  getSelectedLabels,
  getSelectedIndex,
  toggleMultiValue,
  getForceSurfaceBg,
} from './index';

export function useMultiSelectController({
  values,
  options,
  onChange,
  variant,
  filledBg,
  valueMaxRows,
  selectAllValue = null,
}) {
  const listboxId = useId();
  const buttonRef = useRef(null);
  const { rootRef, open, setOpen, toggleOpen } = useDropdownMenu({ focusRef: buttonRef });

  const normalizedOptions = useMemo(() => normalizeOptions(options), [options]);
  const normalizedValues = useMemo(() => normalizeValues(values), [values]);

  const selectedLabels = useMemo(
    () => getSelectedLabels({ normalizedOptions, normalizedValues }),
    [normalizedOptions, normalizedValues],
  );

  const selectedIndex = useMemo(
    () => getSelectedIndex({ normalizedOptions, normalizedValues }),
    [normalizedOptions, normalizedValues],
  );

  const labelStrings = useMemo(() => selectedLabels.map((s) => s.label), [selectedLabels]);
  const selectionIdentityKey = useMemo(
    () => selectedLabels.map((s) => String(s.value)).join('|'),
    [selectedLabels],
  );

  const [highlightedIndex, setHighlightedIndex] = useState(selectedIndex);

  const isEmpty = selectedLabels.length === 0;
  const forceSurfaceBg = getForceSurfaceBg({ variant, filledBg, isEmpty });

  // Every real option except the select-all sentinel, which is a UI affordance, never stored.
  const selectableValues = useMemo(
    () =>
      normalizedOptions
        .map((opt) => String(opt.value))
        .filter((value) => selectAllValue == null || value !== String(selectAllValue)),
    [normalizedOptions, selectAllValue],
  );
  const allSelected = useMemo(
    () =>
      selectableValues.length > 0 && selectableValues.every((v) => normalizedValues.includes(v)),
    [selectableValues, normalizedValues],
  );
  const someSelected = useMemo(
    () => selectableValues.some((v) => normalizedValues.includes(v)),
    [selectableValues, normalizedValues],
  );

  const toggleValue = (nextValue) => {
    if (selectAllValue != null && String(nextValue) === String(selectAllValue)) {
      onChange?.(allSelected ? [] : selectableValues);
      return;
    }
    onChange?.(toggleMultiValue({ normalizedValues, nextValue }));
  };

  const moveHighlight = (delta) =>
    setHighlightedIndex((prev) =>
      moveHighlightIndex({ options: normalizedOptions, fromIndex: prev, delta }),
    );

  const onTriggerKeyDown = (event) =>
    handleSelectTriggerKeyDown({
      event,
      open,
      selectedIndex,
      highlightedIndex,
      options: normalizedOptions,
      setOpen,
      setHighlightedIndex,
      moveHighlight,
      commitChange: toggleValue,
    });

  const onTriggerClick = () => {
    toggleOpen();
    setHighlightedIndex(selectedIndex);
  };

  return {
    listboxId,
    rootRef,
    buttonRef,
    open,
    normalizedOptions,
    normalizedValues,
    labelStrings,
    selectionIdentityKey,
    highlightedIndex,
    setHighlightedIndex,
    isEmpty,
    forceSurfaceBg,
    toggleValue,
    onTriggerKeyDown,
    onTriggerClick,
    valueMaxRows,
    allSelected,
    someSelected,
  };
}
